var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/environment';
import ADD_TENANT_ELEMENT from 'api/mutations/ADD_TENANT_ELEMENT';
import REMOVE_TENANT_ELEMENT from 'api/mutations/REMOVE_TENANT_ELEMENT';
import UPDATE_TENANT_ELEMENT from 'api/mutations/UPDATE_TENANT_ELEMENT';
import GET_CURRENT_TENANT_ELEMENTS from 'api/queries/GET_CURRENT_TENANT_ELEMENTS';
export const initialState = {
    items: [],
    pageInfo: {
        hasNextPage: true,
        endCursor: null,
    },
    status: {
        fetch: 'idle',
        add: 'idle',
        update: 'idle',
        remove: 'idle',
    },
    errors: {
        fetch: null,
        add: null,
        update: null,
        remove: null,
    },
    refetchFlags: {
        fetch: false,
        add: false,
        update: false,
        remove: false,
    },
};
export const fetchElements = createAsyncThunk('elements/fetchElements', (_, { getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const state = getState();
    const { pageInfo } = state.parameters.elements;
    try {
        const response = yield client.query({
            query: GET_CURRENT_TENANT_ELEMENTS,
            variables: {
                first: 25,
                after: pageInfo.endCursor,
            },
            fetchPolicy: 'network-only',
        });
        return response.data.me.currentTenant.elements;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addElement = createAsyncThunk('elements/addElement', ({ tenantId, name }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const elementData = yield client.mutate({
            mutation: ADD_TENANT_ELEMENT,
            variables: { tenantId, name },
            fetchPolicy: 'no-cache',
        });
        return elementData.data.addModelSpecificationTemplateElement
            .modelSpecificationTemplateElement;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
export const updateElement = createAsyncThunk('elements/updateElement', ({ elementId, name }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const elementData = yield client.mutate({
            mutation: UPDATE_TENANT_ELEMENT,
            variables: { elementId, name },
        });
        return elementData.data.updateModelSpecificationTemplateElement
            .modelSpecificationTemplateElement;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
export const removeElement = createAsyncThunk('elements/removeElement', (elementId) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield client.mutate({
            mutation: REMOVE_TENANT_ELEMENT,
            variables: { elementId },
        });
        return elementId;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
const elementsSlice = createSlice({
    name: 'elements',
    initialState,
    reducers: {
        resetElements(state) {
            state.items = [];
            state.pageInfo = initialState.pageInfo;
            state.status = initialState.status;
            state.errors = initialState.errors;
            state.refetchFlags = initialState.refetchFlags;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchElements.pending, (state) => {
            state.status.fetch = 'loading';
            state.refetchFlags.fetch = false;
        })
            .addCase(fetchElements.fulfilled, (state, action) => {
            var _a;
            const newElements = action.payload.edges.map((edge) => edge.node);
            const uniqueElements = newElements.filter((newElement) => !state.items.some((existingElement) => existingElement.id === newElement.id));
            state.items = [...state.items, ...uniqueElements];
            state.pageInfo = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.pageInfo;
            state.status.fetch = 'succeeded';
        })
            .addCase(fetchElements.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch = action.error.message || 'Error fetching components';
        })
            .addCase(addElement.pending, (state) => {
            state.status.add = 'loading';
        })
            .addCase(addElement.fulfilled, (state, action) => {
            state.items = [
                ...state.items,
                Object.assign({}, action.payload),
            ];
            state.status.add = 'succeeded';
        })
            .addCase(addElement.rejected, (state, action) => {
            state.status.add = 'failed';
            state.errors.add = action.error.message || 'Failed to add specification';
        })
            .addCase(updateElement.pending, (state) => {
            state.status.update = 'loading';
        })
            .addCase(updateElement.fulfilled, (state, action) => {
            const { id, name } = action.payload;
            const itemIndex = state.items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                state.items[itemIndex] = Object.assign(Object.assign({}, state.items[itemIndex]), { name });
            }
            state.status.update = 'succeeded';
        })
            .addCase(updateElement.rejected, (state, action) => {
            state.status.update = 'failed';
            state.errors.update = action.error.message || 'Failed to add specification';
        })
            .addCase(removeElement.pending, (state) => {
            state.status.remove = 'loading';
        })
            .addCase(removeElement.fulfilled, (state, action) => {
            state.items = state.items.filter((order) => order.id !== action.payload);
            state.status.remove = 'succeeded';
        })
            .addCase(removeElement.rejected, (state, action) => {
            state.status.remove = 'failed';
            state.errors.remove = action.payload;
        });
    },
});
export const { resetElements } = elementsSlice.actions;
export const selectElements = (state) => state.parameters.elements.items;
export const selectElementById = (state, elementId) => state.parameters.elements.items.find((element) => element.id === elementId);
export const selectElementsPageInfo = (state) => state.parameters.elements.pageInfo;
export const selectElementsStatus = (state) => state.parameters.elements.status;
export const selectElementsErrors = (state) => state.parameters.elements.errors;
export const selectElementsRefetchFlags = (state) => state.parameters.elements.refetchFlags;
export default elementsSlice.reducer;
